import Bottleneck from 'bottleneck/light.js';
import {
  estimateExtractionTokens,
  OPENAI_RPM_BUDGET,
  OPENAI_TPM_BUDGET,
  STUCK_EXTRACTING_MS,
  type ImportFileDto,
} from '@platewell/schema';

/**
 * Paces AI extraction requests so a bulk import stays inside the OpenAI account
 * limits. The budget has to be spent here rather than on the server: the Worker
 * queue consumer autoscales across isolates that cannot see each other, while one
 * browser tab is a single process where an in-memory bucket actually holds.
 *
 * The reservoir is the per-minute token budget and each job's weight is its
 * estimated cost, so one large page draws from the same pool as many small ones.
 */
export function createExtractPacer(): Bottleneck {
  return new Bottleneck({
    reservoir: OPENAI_TPM_BUDGET,
    reservoirRefreshAmount: OPENAI_TPM_BUDGET,
    // Bottleneck requires this to be a multiple of 250ms.
    reservoirRefreshInterval: 60_000,
    // Deliberately unlimited. Bottleneck validates a job's weight against
    // `maxConcurrent` and rejects anything heavier, which would throw on every
    // realistic token estimate. It is also the wrong knob: these requests only
    // enqueue work, so their arrival *rate* is what maps to the API limits, and
    // how many extractions run at once is the queue consumer's business.
    maxConcurrent: null,
    minTime: Math.ceil(60_000 / OPENAI_RPM_BUDGET),
  });
}

/**
 * Files still waiting on an extraction the client has to ask for.
 *
 * `extracting` counts once it is long overdue. A server that dies mid-job leaves the row
 * there with no verdict, and reopening the session is the most natural thing a person
 * does about a stalled import — so it should be what restarts it, rather than a wait for
 * the next sweep. The route applies the same cutoff, so an extraction that is merely slow
 * is left alone either way.
 */
export function needsExtraction(file: ImportFileDto, now: number): boolean {
  if (file.sourceKind === 'json') return false;
  if (file.status === 'pending') return true;
  return file.status === 'extracting' && file.updatedAt < now - STUCK_EXTRACTING_MS;
}

/** Weight one file draws from the per-minute budget. */
export function extractionWeight(file: ImportFileDto): number {
  return estimateExtractionTokens({
    sourceKind: file.sourceKind,
    byteSize: file.byteSize,
  });
}
